import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { applyDiscoveredMappings, discoverMappings } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { ChoiceMapping, DiscoveryResponse, FieldMapping, SyncMode } from "@shared/api-types";
import { SYNC_MODE_LABELS } from "@shared/utils";

interface DiscoveryFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const availableEntities = ["pl_title", "pl_product", "pl_author", "pl_publisher"];

const fieldKey = (mapping: FieldMapping) => mapping.rowKey || `${mapping.entityName}_${mapping.kupField}_${mapping.hsField}`;

const DiscoveryForm = ({ isOpen, onClose, onSuccess }: DiscoveryFormProps) => {
  const [selectedEntities, setSelectedEntities] = useState<string[]>(["pl_title", "pl_product"]);
  const [syncMode, setSyncMode] = useState<SyncMode>(SyncMode.IfEmpty);
  const [preview, setPreview] = useState<DiscoveryResponse | null>(null);
  const [selectedFieldKeys, setSelectedFieldKeys] = useState<Set<string>>(new Set());
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const proposedFieldMappings = preview?.proposedFieldMappings || [];
  const proposedChoiceMappings = preview?.proposedChoiceMappings || [];

  const selectedFieldMappings = useMemo(
    () => proposedFieldMappings.filter((mapping) => selectedFieldKeys.has(fieldKey(mapping))),
    [proposedFieldMappings, selectedFieldKeys],
  );

  const selectedChoiceMappings = useMemo(() => {
    const selectedFields = new Set(
      selectedFieldMappings.map((mapping) => `${mapping.entityName}:${mapping.hsField}`),
    );
    return proposedChoiceMappings.filter((mapping) =>
      selectedFields.has(`${mapping.entityName}:${mapping.fieldName}`),
    );
  }, [proposedChoiceMappings, selectedFieldMappings]);

  const choiceCountByField = useMemo(() => {
    const counts = new Map<string, number>();
    for (const mapping of proposedChoiceMappings) {
      const key = `${mapping.entityName}:${mapping.fieldName}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return counts;
  }, [proposedChoiceMappings]);

  const previewMutation = useMutation<DiscoveryResponse, unknown, void>({
    mutationFn: () => discoverMappings({ action: "discover", entities: selectedEntities, syncMode }),
    onSuccess: (data) => {
      const fields = data.proposedFieldMappings || [];
      setPreview(data);
      setSelectedFieldKeys(new Set(fields.map(fieldKey)));
      toast({
        title: "Discovery preview ready",
        description: `Review ${fields.length} field mappings and ${data.proposedChoiceMappings?.length || 0} choice mappings before applying.`,
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to preview mappings",
        variant: "destructive",
      });
    },
  });

  const applyMutation = useMutation<DiscoveryResponse, unknown, { fieldMappings: FieldMapping[]; choiceMappings: ChoiceMapping[] }>({
    mutationFn: ({ fieldMappings, choiceMappings }) =>
      applyDiscoveredMappings({
        action: "applyDiscovery",
        fieldMappings,
        choiceMappings,
      }),
    onSuccess: (data) => {
      toast({
        title: "Discovery applied",
        description: `Created ${data.fieldMappingsCreated || 0} field mappings and ${data.choiceMappingsCreated || 0} choice mappings`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings", "field"] });
      queryClient.invalidateQueries({ queryKey: ["/api/choice-mappings"] });
      onSuccess();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to apply selected mappings",
        variant: "destructive",
      });
    },
  });

  const handlePreview = (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedEntities.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one entity",
        variant: "destructive",
      });
      return;
    }

    previewMutation.mutate();
  };

  const handleApply = () => {
    applyMutation.mutate({
      fieldMappings: selectedFieldMappings,
      choiceMappings: selectedChoiceMappings,
    });
  };

  const toggleEntity = (entity: string) => {
    setPreview(null);
    setSelectedFieldKeys(new Set());
    setSelectedEntities((prev) =>
      prev.includes(entity) ? prev.filter((e) => e !== entity) : [...prev, entity],
    );
  };

  const toggleField = (mapping: FieldMapping) => {
    const key = fieldKey(mapping);
    setSelectedFieldKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleAllFields = () => {
    if (selectedFieldKeys.size === proposedFieldMappings.length) {
      setSelectedFieldKeys(new Set());
    } else {
      setSelectedFieldKeys(new Set(proposedFieldMappings.map(fieldKey)));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="h-[80vh] max-h-[80vh] w-[80vw] max-w-[80vw] md:max-w-[80vw] lg:max-w-[80vw]">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium">Auto-Discover Mappings</DialogTitle>
        </DialogHeader>

        <form onSubmit={handlePreview} className="flex min-h-0 flex-1 flex-col">
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
            <div className="space-y-5">
              <div>
                <Label className="mb-1 block text-sm font-medium text-gray-700">
                  Entities
                </Label>
                <Card className="border border-gray-300 rounded-md">
                  <CardContent className="h-44 overflow-y-auto p-2 pt-4">
                    {availableEntities.map((entity) => (
                      <div key={entity} className="p-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`entity-${entity}`}
                            checked={selectedEntities.includes(entity)}
                            onCheckedChange={() => toggleEntity(entity)}
                          />
                          <Label htmlFor={`entity-${entity}`} className="text-sm">
                            {entity}
                          </Label>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              <div>
                <Label className="mb-1 block text-sm font-medium text-gray-700">
                  Default Sync Mode
                </Label>
                <Select
                  value={syncMode}
                  onValueChange={(value) => {
                    setPreview(null);
                    setSelectedFieldKeys(new Set());
                    setSyncMode(value as SyncMode);
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select sync mode" />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.values(SyncMode) as SyncMode[]).map((mode) => (
                      <SelectItem key={mode} value={mode}>
                        {SYNC_MODE_LABELS[mode]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#0078D4] hover:bg-[#2B88D8]"
                disabled={previewMutation.isPending}
              >
                {previewMutation.isPending ? "Previewing..." : "Preview Discovery"}
              </Button>
            </div>

            <div className="min-h-0">
              {!preview ? (
                <div className="flex h-full min-h-[520px] items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-600">
                  Run a preview to review proposed mappings before anything is written.
                </div>
              ) : (
                <div className="flex h-full min-h-0 flex-col space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary">{proposedFieldMappings.length} field proposals</Badge>
                      <Badge variant="secondary">{proposedChoiceMappings.length} choice proposals</Badge>
                      <Badge variant="outline">{preview.skipped || 0} skipped existing</Badge>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={toggleAllFields}>
                      {selectedFieldKeys.size === proposedFieldMappings.length ? "Clear All" : "Select All"}
                    </Button>
                  </div>

                  <div className="min-h-0 flex-1 overflow-auto rounded-md border">
                    <Table className="min-w-[920px]">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12"></TableHead>
                          <TableHead>Entity</TableHead>
                          <TableHead>KUP Field</TableHead>
                          <TableHead>HS Field</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">Choices</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {proposedFieldMappings.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="py-8 text-center text-sm text-gray-500">
                              No new field mappings found.
                            </TableCell>
                          </TableRow>
                        ) : (
                          proposedFieldMappings.map((mapping) => {
                            const key = fieldKey(mapping);
                            const choiceCount = choiceCountByField.get(`${mapping.entityName}:${mapping.hsField}`) || 0;
                            return (
                              <TableRow key={key}>
                                <TableCell>
                                  <Checkbox
                                    checked={selectedFieldKeys.has(key)}
                                    onCheckedChange={() => toggleField(mapping)}
                                  />
                                </TableCell>
                                <TableCell className="whitespace-nowrap">{mapping.entityName}</TableCell>
                                <TableCell className="font-mono text-xs">{mapping.kupField}</TableCell>
                                <TableCell className="font-mono text-xs">{mapping.hsField}</TableCell>
                                <TableCell>{mapping.isChoiceField ? "Picklist" : "Field"}</TableCell>
                                <TableCell className="text-right">{choiceCount}</TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="text-sm text-gray-600">
                    Selected: {selectedFieldMappings.length} field mappings and {selectedChoiceMappings.length} choice mappings.
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-[#0078D4] hover:bg-[#2B88D8]"
              disabled={!preview || selectedFieldMappings.length === 0 || applyMutation.isPending}
              onClick={handleApply}
            >
              {applyMutation.isPending ? "Applying..." : "Apply Selected"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DiscoveryForm;
